import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  CircleCheck,
  CircleX,
  Clock,
  MapPin,
  MessageCircle,
  Phone,
  User,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/format';
import { useFeedback } from '@/components/Feedback';
import { EmptyState, HeroHeader, Lightbox } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Applications.module.css';

type Application = {
  id: string;
  user_id: string;
  full_name: string;
  phone: string;
  city: string;
  services: string[];
  experience_years: number;
  description: string;
  document_url: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
};

type Filter = 'pending' | 'approved' | 'rejected' | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'pending', label: 'En attente' },
  { key: 'approved', label: 'Approuvés' },
  { key: 'rejected', label: 'Rejetés' },
  { key: 'all', label: 'Tous' },
];

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: '#FFA900', Icon: Clock },
  approved: { label: 'Approuvé', color: '#4CAF50', Icon: CircleCheck },
  rejected: { label: 'Rejeté', color: '#F44336', Icon: CircleX },
};

export default function AdminApplications() {
  const { toast, confirm } = useFeedback();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('pending');
  const [lightbox, setLightbox] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('provider_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') query = query.eq('status', filter);

      const { data, error } = await query;
      if (!error && data) setApplications(data as Application[]);
    } catch (err) {
      console.error('Error loading applications:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (application: Application) => {
    const ok = await confirm({
      title: `Approuver ${application.full_name} ?`,
      body: 'Le compte deviendra prestataire et sera visible par les clients.',
      confirmLabel: 'Approuver',
    });
    if (!ok) return;

    setProcessingId(application.id);
    try {
      const { error: applicationError } = await supabase
        .from('provider_applications')
        .update({ status: 'approved' })
        .eq('id', application.id);
      if (applicationError) throw new Error(applicationError.message);

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ account_type: 'provider' })
        .eq('id', application.user_id);
      if (profileError) throw new Error(profileError.message);

      const { data: existing } = await supabase
        .from('providers')
        .select('id')
        .eq('id', application.user_id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('providers')
          .update({
            is_approved: true,
            description: application.description,
            experience_years: application.experience_years,
          })
          .eq('id', application.user_id);
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.from('providers').insert({
          id: application.user_id,
          description: application.description,
          experience_years: application.experience_years,
          is_approved: true,
        });
        if (error) throw new Error(error.message);
      }

      // Copy the application city onto the profile when it has none yet.
      await supabase
        .from('profiles')
        .update({ city: application.city })
        .eq('id', application.user_id)
        .is('city', null);

      if (application.services?.length > 0) {
        const { data: serviceRows } = await supabase
          .from('services')
          .select('id, name')
          .in('name', application.services);

        if (serviceRows && serviceRows.length > 0) {
          await supabase.from('provider_services').upsert(
            serviceRows.map((row: any) => ({
              provider_id: application.user_id,
              service_id: row.id,
            })),
            { onConflict: 'provider_id,service_id' }
          );
        }
      }

      toast('success', 'Demande approuvée', `${application.full_name} est maintenant prestataire.`);
      await load();
    } catch (err) {
      toast('error', "Impossible d'approuver", err instanceof Error ? err.message : undefined);
    } finally {
      setProcessingId(null);
    }
  };

  const reject = async (application: Application) => {
    const ok = await confirm({
      title: `Rejeter ${application.full_name} ?`,
      confirmLabel: 'Rejeter',
      destructive: true,
    });
    if (!ok) return;

    setProcessingId(application.id);
    try {
      const { error } = await supabase
        .from('provider_applications')
        .update({ status: 'rejected' })
        .eq('id', application.id);
      if (error) throw new Error(error.message);
      toast('info', 'Demande rejetée');
      await load();
    } catch (err) {
      toast('error', 'Impossible de rejeter', err instanceof Error ? err.message : undefined);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className={styles.page}>
      <HeroHeader title="Demandes prestataires" subtitle="Vérifiez et validez les candidatures." />

      <div className={styles.body}>
        <div className={styles.filters}>
          {FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={filter === item.key ? 'chip chip--on' : 'chip'}
              onClick={() => setFilter(item.key)}
              aria-pressed={filter === item.key}
            >
              {item.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Spinner block />
        ) : applications.length === 0 ? (
          <EmptyState icon={<User size={30} />} title="Aucune demande" />
        ) : (
          <ul className={styles.list}>
            {applications.map((application) => {
              const config = STATUS_CONFIG[application.status];
              const StatusIcon = config.Icon;
              const busy = processingId === application.id;

              return (
                <li key={application.id} className={styles.card}>
                  <div className={styles.cardTop}>
                    <span
                      className={styles.badge}
                      style={{ background: `${config.color}22`, color: config.color }}
                    >
                      <StatusIcon size={13} /> {config.label}
                    </span>
                    <span className={styles.date}>{formatDate(application.created_at)}</span>
                  </div>

                  <ul className={styles.infoList}>
                    <li className={styles.infoRow}>
                      <User size={14} color="var(--navy)" /> {application.full_name}
                    </li>
                    <li className={styles.infoRow}>
                      <Phone size={14} color="var(--navy)" /> {application.phone}
                    </li>
                    <li className={styles.infoRow}>
                      <MapPin size={14} color="var(--navy)" /> {application.city}
                    </li>
                    <li className={styles.infoRow}>
                      <Briefcase size={14} color="var(--navy)" />
                      {application.services?.join(', ') || '—'} · {application.experience_years} ans
                    </li>
                  </ul>

                  {application.description ? (
                    <p className={styles.description}>{application.description}</p>
                  ) : null}

                  {application.document_url ? (
                    <button
                      type="button"
                      className={styles.cinBtn}
                      onClick={() => setLightbox(application.document_url)}
                    >
                      <span className={styles.cinLabel}>Photo CIN</span>
                      <img className={styles.cinThumb} src={application.document_url} alt="CIN" />
                    </button>
                  ) : (
                    <p className={styles.cinMissing}>Aucune photo CIN jointe</p>
                  )}

                  <div className={styles.actions}>
                    <Link className="btn btn--outline btn--sm" to={`/admin/chat/${application.user_id}`}>
                      <MessageCircle size={15} /> Contacter
                    </Link>

                    {application.status === 'pending' ? (
                      <>
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          onClick={() => void reject(application)}
                          disabled={busy}
                        >
                          <CircleX size={15} /> Rejeter
                        </button>
                        <button
                          type="button"
                          className="btn btn--green btn--sm"
                          onClick={() => void approve(application)}
                          disabled={busy}
                        >
                          {busy ? (
                            <Spinner size={14} color="#fff" />
                          ) : (
                            <>
                              <CircleCheck size={15} /> Approuver
                            </>
                          )}
                        </button>
                      </>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
